import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Plus } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { logError } from "@/lib/log";

export const SupporterOpenWhenForm = ({ token, name }) => {
  const [info, setInfo] = useState(null);
  const [topic, setTopic] = useState("");
  const [custom, setCustom] = useState(false);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/supporters/view/${token}/open-when`).then((r) => { setInfo(r.data); setTopic((t) => t || r.data.topics[1]); })
    .catch((e) => logError("Supporter open-when failed:", e)), [token]);
  useEffect(() => { load(); }, [load]);

  const send = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await api.post(`/supporters/view/${token}/open-when`, { topic: topic.trim(), body: body.trim() }); setBody(""); toast.success(`Letter sent 💌 ${name} can open it whenever they need it`); load(); }
    catch (err) { toast.error(errMsg(err, "Could not send your letter")); }
    finally { setBusy(false); }
  };

  if (!info?.enabled) return null;
  return (
    <form onSubmit={send} className="mm-card p-6" data-testid="supporter-open-when-form">
      <h2 className="font-semibold">💝 Write an Open-When letter for {name}</h2>
      <p className="text-sm text-mm-muted mt-1">It waits in {name}'s Letters, sealed, until they need it. They'll only see the topic until they open it.</p>
      <div className="text-sm text-mm-muted mt-3">Open when…</div>
      <div className="flex flex-wrap gap-2 mt-2">
        {info.topics.map((t, i) => <button type="button" key={t} onClick={() => { setTopic(t); setCustom(false); }} className={`mm-chip !text-xs ${!custom && topic === t ? "mm-chip-on" : ""}`} data-testid={`supporter-ow-topic-${i}`}>{t}</button>)}
        <button type="button" onClick={() => { setCustom(true); setTopic(""); }} className={`mm-chip !text-xs ${custom ? "mm-chip-on" : ""}`} data-testid="supporter-ow-topic-custom"><Plus size={12} />Your own</button>
      </div>
      {custom && <input className="mm-input mt-3" maxLength={80} value={topic} onChange={(e) => setTopic(e.target.value)} placeholder="Open when… you miss home" data-testid="supporter-ow-custom-input" />}
      <textarea className="mm-input min-h-[140px] mt-3" maxLength={2000} value={body} onChange={(e) => setBody(e.target.value)} placeholder={`Dear ${name}, if you're reading this…`} data-testid="supporter-ow-body" />
      <div className="flex justify-between items-center mt-3">
        <span className="text-xs text-mm-muted">{body.length}/2000</span>
        <button className="mm-btn" disabled={busy || !topic.trim() || !body.trim()} data-testid="supporter-ow-send">{busy && <Loader2 size={16} className="animate-spin" />}Send letter</button>
      </div>
      {info.sent.length > 0 && (
        <div className="mt-4" data-testid="supporter-ow-sent">
          <div className="text-xs font-semibold text-mm-muted mb-2">Letters you've sent</div>
          <ul className="space-y-1.5">
            {info.sent.map((l) => (
              <li key={l.id} className="flex items-center justify-between gap-3 text-sm mm-soft px-3 py-2 rounded-xl" data-testid={`supporter-ow-sent-${l.id}`}>
                <span className="min-w-0 break-words [overflow-wrap:anywhere]">Open when… {l.topic}</span>
                <span className={`shrink-0 text-xs font-semibold ${l.opened ? "text-[#B23A63]" : "text-mm-muted"}`} data-testid={`supporter-ow-status-${l.id}`}>{l.opened ? "💌 Opened" : "Sealed"}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </form>
  );
};
